"use client";

import { useEffect, useState, type ReactNode } from "react";
import { PublicEditorialInlineEditorProvider } from "@/components/site/public-editorial-inline-editor";
import { HomepageEditorProvider } from "@/components/site/homepage-editor";
import type { PublicEditorArticle, PublicEditorSection } from "@/lib/public-editorial-types";

type CanEditResponse = {
  canEdit: boolean;
  role?: "ADMIN" | "EDITOR";
  articles?: PublicEditorArticle[];
  sections?: PublicEditorSection[];
};

export function AdminEditorialEditorGate({
  routePath,
  categorySlug,
  toggleClassName,
  children,
}: {
  routePath: string;
  categorySlug?: string | null;
  toggleClassName?: string;
  children: ReactNode;
}) {
  const [editorData, setEditorData] = useState<CanEditResponse | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const params = new URLSearchParams({ routePath });
    if (categorySlug) params.set("categorySlug", categorySlug);

    async function loadCanEdit() {
      try {
        const response = await fetch(`/api/admin/editorial/can-edit?${params.toString()}`, {
          credentials: "same-origin",
          cache: "no-store",
          signal: controller.signal,
        });
        const data = (await response.json()) as CanEditResponse;
        if (!controller.signal.aborted) {
          setEditorData(data);
          if (process.env.NODE_ENV !== "production") {
            console.info(`[public-editorial-editor] routePath=${routePath} canEdit=${Boolean(data.canEdit)}`);
          }
        }
      } catch {
        if (!controller.signal.aborted) {
          setEditorData({ canEdit: false });
          if (process.env.NODE_ENV !== "production") {
            console.info(`[public-editorial-editor] routePath=${routePath} canEdit=false`);
          }
        }
      }
    }

    loadCanEdit();
    return () => controller.abort();
  }, [categorySlug, routePath]);

  if (!editorData?.canEdit) return <>{children}</>;

  const content = routePath === "/" ? <HomepageEditorProvider>{children}</HomepageEditorProvider> : children;

  return (
    <PublicEditorialInlineEditorProvider
      key={routePath}
      routePath={routePath}
      articles={editorData.articles ?? []}
      sections={editorData.sections ?? []}
      categorySlug={categorySlug}
      toggleClassName={toggleClassName}
    >
      {content}
    </PublicEditorialInlineEditorProvider>
  );
}
